import React from "react";
import LocationModal from "../location-modal/LocationModal.js";
import PickupDeliveryModal from "../pickup-delivery-modal/PickupDeliveryModal.js";
import MethodChangeModal from "../method-change-modal/MethodChangeModal.js";
import LoyaltyModal from "../loyalty-modal/LoyaltyModal.js";
import * as CommonUtilities from "../../../common/utils/CommonUtils.js";
import BrandLogo from "../../../common/ui/BrandLogo.js";

/* =============================================================================
   Store header.
   -----------------------------------------------------------------------------
   ⚠ SIX SITUATIONS, NOT ONE. urlConfig.Type uses obfuscated codes, which is why
   searching the code for "kiosk" finds nothing:

     '' | null | 'null' | 'web'   web ordering — switcher + pickup/delivery
     'k7s5k'                      kiosk
     'q8r6e'                      QR code
     't9b6f'                      table service
     anything else                static store name
     + wardinfo                   ward ordering, independent of Type

   The header tells the customer where they are and offers only what they can
   actually act on. A kiosk user cannot change store; a ward patient cannot
   choose delivery. Showing those controls disabled would be worse than not
   showing them.

   ⚠ Delivery is DoorDash white-label and usually absent. The method control
   renders only when availability has been checked AND the brand has it enabled.

   ⚠ In delivery mode the store is FIXED. Only pickup offers the switcher.

   ⚠ The method control keeps .btn-check / .btn-outline-primary. App.js injects
   the brand colour straight into those two selectors, so replacing them would
   silently un-brand the control. The wrapper is restyled; the classes stay.
   ============================================================================= */

const WEB_TYPES = ['', null, 'null', 'web'];
const KIOSK = 'k7s5k';
const QR = 'q8r6e';
const TABLE = 't9b6f';

export default function MenuNavigation(props) {
    /* ⚠ The category rail and the basket rail both stick BELOW this header, so
       they need its height. It is not a constant: the header grows a caption
       line in table / kiosk / QR / ward / delivery modes, loses the method
       control when DoorDash is off, and is shorter on a phone. A hardcoded
       value was 118px against a real 143px, so the rail parked 25px behind the
       header and stayed there — exactly the bug this fixes.

       Measured here and published as --wo-head-h on the document root, so the
       CSS never has to guess. */
    const headRef = React.useRef(null);
    React.useEffect(() => {
        const el = headRef.current;
        if (!el) { return; }
        const apply = () => {
            const h = Math.round(el.getBoundingClientRect().height);
            if (h > 0) {
                document.documentElement.style.setProperty('--wo-head-h', `${h}px`);
            }
        };
        apply();
        if (typeof ResizeObserver === 'undefined') { // older Safari
            window.addEventListener('resize', apply);
            return () => window.removeEventListener('resize', apply);
        }
        const ro = new ResizeObserver(apply);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const type = props.urlConfig.Type;
    const isWeb = WEB_TYPES.indexOf(type) !== -1;
    const isDelivery = props.pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === 'delivery';
    const hasWard = props.urlConfig.WardInfo !== undefined
        && props.urlConfig.WardInfo !== null
        && props.urlConfig.WardInfo !== 'null'
        && props.urlConfig.WardInfo !== '0'
        && props.urlConfig.WardInfo !== '';

    /* ---- where am I? ------------------------------------------------------ */
    // eyebrow sits above the name; caption sits below and explains fulfilment.
    let eyebrow = null;
    let title = props.currentLocation.LocationName;
    let caption = null;
    let canChangeStore = false;

    if (hasWard) {
        eyebrow = 'Ordering for';
        caption = 'Delivered to your bed at mealtime';
    } else if (type === TABLE) {
        eyebrow = 'Ordering at';
        title = `${props.currentLocation.LocationName}`;
        caption = `Table ${props.urlConfig.TableNumber} · your order comes to your table`;
    } else if (type === KIOSK) {
        eyebrow = 'Ordering at';
        caption = 'Collect from the counter when called';
    } else if (type === QR) {
        eyebrow = 'Ordering at';
        caption = 'Scanned in store';
    } else if (isWeb && isDelivery) {
        eyebrow = 'Delivering from';
        caption = props.pickupOrDelivery.DeliveryStatus.EstimateDeliveryTime
            ? `Estimated delivery ${props.pickupOrDelivery.DeliveryStatus.EstimateDeliveryTime}`
            : 'Delivery to your address';
    } else if (isWeb) {
        eyebrow = 'Ordering from · tap to change';
        canChangeStore = true;
    } else {
        eyebrow = 'Ordering from';
    }

    const pin =
        <span className="wo-head__pin" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z" />
                <circle cx="12" cy="10" r="2.6" />
            </svg>
        </span>;

    const whereInner =
        <>
            {pin}
            <span className="wo-head__where">
                <span className="wo-head__eyebrow">{eyebrow}</span>
                <span className="wo-head__name">
                    {title}
                    {canChangeStore &&
                        <svg className="wo-head__chev" width="17" height="17" viewBox="0 0 24 24"
                             fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"
                             aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>}
                </span>
                {caption && <span className="wo-head__caption">{caption}</span>}
            </span>
        </>;

    const where = canChangeStore
        ? <button type="button" className="wo-head__store wo-head__store--btn"
                  onClick={props.handleShowLocModal} aria-haspopup="dialog">{whereInner}</button>
        : <div className="wo-head__store">{whereInner}</div>;

    /* ---- pickup / delivery ------------------------------------------------ */
    // Only web ordering, only once DoorDash availability is known and enabled.
    const showMethod = isWeb
        && props.pickupOrDelivery.PickupDeliveryModal.DeliveryAvailabilityHasBeenChecked
        && props.pickupOrDelivery.Config.EnableDoorDashWhiteLabelDelivery;

    const method = showMethod
        ? <div className="wo-seg btn-group" role="group" aria-label="Pick up or delivery">
            <input type="radio" className="btn-check" name="pickupdelivery" id="btnRadioPickup"
                   data-value="pickup" onChange={props.handlePickupDeliverySwitch}
                   checked={!isDelivery} />
            <label className="btn btn-outline-primary" htmlFor="btnRadioPickup">Pick Up</label>
            <input type="radio" className="btn-check" name="pickupdelivery" id="btnRadioDelivery"
                   data-value="delivery" onChange={props.handlePickupDeliverySwitch}
                   checked={isDelivery} />
            <label className="btn btn-outline-primary" htmlFor="btnRadioDelivery">Delivery</label>
          </div>
        : null;

    /* ---- actions ---------------------------------------------------------- */
    const loyalty = CommonUtilities.StaticFields.EnableBrandLoyalty
        ? <button type="button" className="wo-head__ghost" onClick={props.handleClickOpenLoyaltyModal}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.6-6 8-6s8 2 8 6" />
            </svg>
            <span>{props.loyaltyMember.MenuNav.LoyaltyMemberButtonValue}</span>
          </button>
        : null;

    return (
        <>
            <header className="wo-head" ref={headRef}>
                <div className="wo-head__inner">
                    <div className="wo-head__top">
                        {/* ⚠ TWO SOURCES, and both are live. The uploaded logo from
                            the S8MC Website Config screen when the brand has set
                            one; otherwise images/BrandLogo_<BRAND>.png, swapped at
                            build time, which is what all eleven live sites still
                            use. The build-time file is NOT superseded — it is the
                            fallback for every brand that has not opened that screen,
                            so removing it would leave those sites unbranded.
                            See S8MC Specs\Website Config. */}
                        <span className="wo-head__logo">
                            <BrandLogo
                                src={props.websiteConfig ? props.websiteConfig.logoURL : null}
                                width={props.websiteConfig ? props.websiteConfig.logoWidth : null}
                                height={props.websiteConfig ? props.websiteConfig.logoHeight : null}
                            />
                        </span>
                        {where}
                        <div className="wo-head__actions">
                            {method}
                            {loyalty}
                            <button type="button" className="wo-head__cart" onClick={props.handleViewCart}
                                    disabled={props.amountInCart < 1} aria-label="View basket">
                                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                                    <path d="M3 3h2l2.4 11.4a2 2 0 0 0 2 1.6h8.2a2 2 0 0 0 2-1.6L21 7H6" />
                                    <circle cx="10" cy="20" r="1.4" /><circle cx="18" cy="20" r="1.4" />
                                </svg>
                                <span className="wo-head__cartLabel">Basket</span>
                                <span className="wo-head__cartCount">{props.amountInCart}</span>
                            </button>
                        </div>
                    </div>

                    {/* Search is new. Client-side over name and description only — see the audit. */}
                    <div className="wo-search">
                        <span className="wo-search__icon" aria-hidden="true">
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="2" strokeLinecap="round">
                                <circle cx="11" cy="11" r="7" /><path d="m20 20-3.2-3.2" />
                            </svg>
                        </span>
                        <input type="search" className="wo-search__input" placeholder="Search the menu"
                               aria-label="Search the menu"
                               value={props.menuSearchTerm || ''}
                               onChange={props.handleMenuSearch} />
                    </div>
                </div>
            </header>

            <LocationModal
                locationList={props.locationList}
                getGeoLocation={props.getGeoLocation}
                locationLoading={props.locationLoading}
                locationSearchInputRef={props.locationSearchInputRef}
                handleFocusSearchLocation={props.handleFocusSearchLocation}
                handleSearchLocation={props.handleSearchLocation}
                handleClearSearchLocation={props.handleClearSearchLocation}
                handleToggleOpenLocation={props.handleToggleOpenLocation}
                handleSelectLocation={props.handleSelectLocation}
                currentLocation={props.currentLocation}
                handleShowLocModal={props.handleShowLocModal}
                show={props.showLocModal}
                handleCloseLocModal={props.handleCloseLocModal}
                locationError={props.locationError}
                menuError={props.menuError}
                handleBackPickupLocationsToMethodClick={props.handleBackPickupLocationsToMethodClick}
                pickupOrDelivery={props.pickupOrDelivery}
            />
            <PickupDeliveryModal
                pickupOrDelivery={props.pickupOrDelivery}
                handleSelectMethodPickupDeliveryModal={props.handleSelectMethodPickupDeliveryModal}
                handleChangeAddressFields={props.handleChangeAddressFields}
                handleSubmitAddressFields={props.handleSubmitAddressFields}
                handleBackAddressToMethodClick={props.handleBackAddressToMethodClick}
            />
            <LoyaltyModal
                loyaltyMember={props.loyaltyMember}
                handleClickCloseLoyaltyModal={props.handleClickCloseLoyaltyModal}
                currentLocation={props.currentLocation}
                handleClickLoginLoyaltyMobile={props.handleClickLoginLoyaltyMobile}
                handleChangeMobileFields={props.handleChangeMobileFields}
                handleNextValidateMobile={props.handleNextValidateMobile}
                handleBackOtpPage={props.handleBackOtpPage}
                handleChangeOtpFields={props.handleChangeOtpFields}
                handleResendOtpToMobile={props.handleResendOtpToMobile}
                handleNextValidateOtp={props.handleNextValidateOtp}
                handleLoyaltyLogout={props.handleLoyaltyLogout}
                handleSelectVoucher={props.handleSelectVoucher}
                handleBackItemVoucherParent={props.handleBackItemVoucherParent}
                handleSelectGroupItemVoucherParent={props.handleSelectGroupItemVoucherParent}
                handleAddVoucherToCartItemVoucherParent={props.handleAddVoucherToCartItemVoucherParent}
                handleBackItemVoucherProductList={props.handleBackItemVoucherProductList}
                handleSelectProductItemVoucherProductList={props.handleSelectProductItemVoucherProductList}
                handleBackItemVoucherChild={props.handleBackItemVoucherChild}
                handleModChangeItemVoucherChild={props.handleModChangeItemVoucherChild}
                handleAddToVoucherItemVoucherChild={props.handleAddToVoucherItemVoucherChild}
            />
            <MethodChangeModal
                pickupOrDelivery={props.pickupOrDelivery}
                handleCloseMethodChangeModal={props.handleCloseMethodChangeModal}
                handleConfirmMethodChange={props.handleConfirmMethodChange}
            />
        </>
    );
}
